import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import '@/App.css';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import ReferentDashboard from './pages/ReferentDashboard';
import AnimateurDashboard from './pages/AnimateurDashboard';
import FinanceurDashboard from './pages/FinanceurDashboard';
import ApprenantDashboard from './pages/ApprenantDashboard';
import EntreprisesPage from './pages/EntreprisesPage';
import InvitationConfirmation from './pages/InvitationConfirmation';
import SetupPassword from './pages/SetupPassword';
import ConsentementDispositif from './components/ConsentementDispositif';
import MainLayout from './components/MainLayout';
import ForcePasswordChange from './components/ForcePasswordChange';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionProvider, usePermissions } from './context/PermissionContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';

// Composant pour le modal de changement de mot de passe global
const PasswordChangeModal = () => {
  const { user, mustChangePassword, onPasswordChanged } = useAuth();
  
  if (!mustChangePassword || !user) return null;
  
  return (
    <ForcePasswordChange
      open={mustChangePassword}
      onSuccess={onPasswordChanged}
      userName={`${user.prenom} ${user.nom}`}
    />
  );
};

// Composant pour rediriger vers login en préservant les query params
const RedirectToLogin = () => {
  const location = useLocation();
  return <Navigate to={`/login${location.search}`} replace />;
};

// Wrapper pour la page de consentement (lit le token depuis l'URL)
const ConsentementDispositifPage = () => {
  const { token } = useParams();
  return <ConsentementDispositif token={token} />;
};

const PrivateRoute = ({ children, allowedRoles, requiredPermission }) => {
  const { user, loading } = useAuth();
  const { hasPermission, loading: permLoading } = usePermissions();
  const location = useLocation();

  if (loading || permLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/login${location.search}`} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={`/login${location.search}`} replace />;
  }

  // Vérifier la permission si spécifiée
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={`/login${location.search}`} replace />;
  }

  // Envelopper avec MainLayout pour afficher les logos FSE+ sur toutes les pages
  return <MainLayout>{children}</MainLayout>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/admin"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/referent"
        element={
          <PrivateRoute allowedRoles={['referent']}>
            <ReferentDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/animateur"
        element={
          <PrivateRoute allowedRoles={['animateur']}>
            <AnimateurDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/financeur"
        element={
          <PrivateRoute allowedRoles={['financeur']}>
            <FinanceurDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/bénéficiaire"
        element={
          <PrivateRoute allowedRoles={['bénéficiaire']}>
            <ApprenantDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/entreprises"
        element={
          <PrivateRoute allowedRoles={['admin', 'referent', 'animateur']}>
            <EntreprisesPage />
          </PrivateRoute>
        }
      />
      {/* Route publique pour confirmation d'invitation */}
      <Route path="/invitation" element={<InvitationConfirmation />} />
      {/* Route publique pour création de mot de passe */}
      <Route path="/setup-password" element={<SetupPassword />} />
      {/* Route publique : formulaire de consentement entrée dispositif (sans connexion) */}
      <Route
        path="/consentement/:token"
        element={<ConsentementDispositifPage />}
      />
      <Route path="/" element={<RedirectToLogin />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PermissionProvider>
          <BrowserRouter>
            <AppRoutes />
            <PasswordChangeModal />
            <Toaster position="top-right" richColors closeButton />
          </BrowserRouter>
        </PermissionProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;